import { useEventUserRole } from '@shared/components/AuthProvider';
import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { useUnlockDialogContext } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialog.context';
import { useCtaButtons } from '@apps/guest/packages/layout-engine/common/utils/useCtaButtons';
import { useGetEventCTAButtonsDataQuery } from '@apps/guest/packages/layout-engine/components/CtaButtons/CtaButtons.graphql.generated';
import { useMigratedCustomPageFeatureFlags } from '@shared/hooks/useMigratedCustomPageFeatureFlags/useMigratedCustomPageFeatureFlags';
import { useAuthUpgradeRefetch } from '@shared/hooks/useAuthUpgradeQuery';
import { useEffectiveGqlCacheData } from '@shared/hooks/useEffectiveGqlCacheData';
import { useIsGuestSiteSsrEnabled } from '@apps/guest/shared/hooks/useIsGuestSiteSsrEnabled';
import queryString from '@shared/core/queryString';
import { useFetchScheduleListData } from './useFetchScheduleListData';

// Use the explicit ?preview=true query param rather than isInIframe() — iframes can appear
// in other legitimate contexts (e.g. masked/custom domains) that are not preview sessions.
const isPreviewing = queryString.getValueString('preview') === 'true';

export const useScheduleListController = (eventHandle: string) => {
  const { hasIdentifiedUserOnce } = useEventUserRole();
  const { handleUnlockDialogOpen } = useUnlockDialogContext();
  const { getUnlockButtonText } = useCtaButtons();
  const { isMigratedCustomPageEnabled, isLoading: isFlagsLoading } = useMigratedCustomPageFeatureFlags();
  // Use eventName from useEventNameMatch so the cache key matches CtaButtons' own query variable.
  const { eventName } = useEventNameMatch();

  // Read page privacy from GetEventCTAButtonsData already fired by CtaButtons — no extra network request.
  // ssr:false matches CtaButtons' own query options to ensure Apollo uses the same cache entry.
  const { data: ctaData, loading: ctaLoading } = useGetEventCTAButtonsDataQuery({
    variables: { name: eventName || '' },
    batchMode: 'fast',
    ssr: false,
  });

  // When the schedule is a migrated custom page it appears in customPages (type: custom, slug: 'schedule')
  // rather than schedulePage (type: schedule). Use the correct source based on the feature flag.
  // If the flag is on but the event isn't actually migrated, customPages won't have 'schedule' — fall
  // back to schedulePage so we don't incorrectly skip the password gate on a private schedule page.
  // While either source is still loading, default to true (treat as private) so we never
  // accidentally skip the password gate before we know the page's actual privacy state.
  const schedulePageIsPrivate =
    isFlagsLoading || ctaLoading
      ? true
      : isMigratedCustomPageEnabled('schedule')
        ? (ctaData?.eventByName?.customPages?.find(p => p?.page?.pageSlug === 'schedule')?.private ?? ctaData?.eventByName?.schedulePage?.private ?? true)
        : (ctaData?.eventByName?.schedulePage?.private ?? true);

  const isGuestSiteSsrEnabled = useIsGuestSiteSsrEnabled();

  // In preview mode (isPreviewing = true), SSR is disabled and GetGuestSiteSchedule fires
  // immediately with isAdmin: false before GetMe resolves. Skip until auth is known to
  // prevent refetchQueries from landing with the wrong isAdmin variable.
  const { data, previousData, loading, refetch } = useFetchScheduleListData(eventHandle, isPreviewing && !hasIdentifiedUserOnce);

  // SSR public-first: When SSR renders /schedule, the query fires with anonymous context.
  // activeSession fields (eventCTA.scheduleHasInviteOnlyItems, person, hasUnlocked) will
  // reflect anonymous defaults. After GetMe resolves, refetch to get auth-aware data
  // (invite-only filtering, guest identity).
  useAuthUpgradeRefetch(isGuestSiteSsrEnabled ? refetch : undefined);

  // When isAdmin variable changes (false → true after GetMe), Apollo sees a new cache key
  // and data becomes undefined until the network response arrives. useEffectiveGqlCacheData
  // falls back to previousData during this transition and suppresses loading when we have
  // usable data — preventing UI flash and loading oscillation from cache-and-network.
  const { effectiveData, loading: effectiveLoading } = useEffectiveGqlCacheData({ data, previousData, loading });

  const { activeSession, formattedSchedules, toBeAnnouncedItems } = effectiveData ?? {};
  const { eventCTA, hasUnlocked, person } = activeSession || {};

  const tbaSchedule = toBeAnnouncedItems ?? [];
  const formattedSchedule = formattedSchedules ?? [];

  const scheduleHasPrivateItems = !!eventCTA?.scheduleHasInviteOnlyItems;
  const hasGuestSession = !!person?.fullName?.length;

  const shouldDisplayUnlockButton = !isPreviewing && scheduleHasPrivateItems && !hasGuestSession;
  const unlockButtonText = getUnlockButtonText(hasUnlocked);

  return {
    formattedSchedule,
    shouldDisplayUnlockButton,
    loading: effectiveLoading,
    tbaSchedule,
    // Skip the password gate only when the schedule page itself is public.
    // If the page is private, guests must enter a password first (via UnlockForm).
    handleUnlockDialogClick: () => handleUnlockDialogOpen(true, !schedulePageIsPrivate),
    unlockButtonText,
  };
};
